import React from "react";
import { useState, useEffect } from "react";
import dayjs, { Dayjs } from "dayjs";
import Card from "../components/Card";
import DatePickerComponent from "../components/DatePicker";
import Dropdown from "../components/Dropdown";
import {
  checkIfEventIsBetweenDates,
  hideExipredEvents,
  sortByDateTime,
  readableDate,
} from "../hooks/DateTime";
import {
  LOCATIONS,
  COUNTRIES,
  getRegionsByCountry,
  getEventsByRegion,
  getEventsByCountry,
} from "../constants/locations";
import { useData } from "../contexts/DataContext";
import { eventTypes, getByType, getEventTypeLabel } from "../constants/eventTypes";
import ButtonSlim from "../components/Buttons/ButtonSlim";
import { useNavigate } from "react-router-dom";

const Events: React.FC = () => {
  const { eventsData, loading } = useData();
  const [selectedStartDate, setSelectedStartDate] = useState<Dayjs>(dayjs());
  const [selectedEndDate, setSelectedEndDate] = useState<Dayjs | null>(null);
  const [filteredEvents, setFilteredEvents] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [regions, setRegions] = useState<any>(
    Object.values(LOCATIONS).map((location) => location.region)
  );
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [selectedEventType, setSelectedEventType] = useState<string | null>(
    null
  );
  const navigate = useNavigate();

  useEffect(() => {
    if (eventsData.length > 0) {
      setEvents(sortByDateTime(hideExipredEvents(eventsData)));
    }
  }, [eventsData]);

  const handleDateChange = (date: any) => {
    if (date) {
      setSelectedStartDate(dayjs(date.startDate).startOf("day"));
      setSelectedEndDate(dayjs(date.endDate).endOf("day"));
    } else {
      // Reset dates if the date picker is cleared
      setSelectedStartDate(dayjs());
      setSelectedEndDate(null);
    }
  };

  useEffect(() => {
    if (selectedCountry) {
      const regions = getRegionsByCountry(selectedCountry);
      setRegions(regions);
      // Reset selectedRegion if it's not in the new cities list
      if (selectedRegion && !regions.includes(selectedRegion)) {
        setSelectedRegion(null);
      }
    } else {
      // Reset to all cities when no country is selected
      setRegions(Object.values(LOCATIONS).map((location) => location.region));
    }
  }, [selectedCountry]);

  useEffect(() => {
    const filtered = events.filter((event) =>
      checkIfEventIsBetweenDates(event, selectedStartDate, selectedEndDate)
    );
    setFilteredEvents(filtered);
  }, [selectedStartDate, selectedEndDate, events]);

  useEffect(() => {
    const filtered = getEventsByRegion(selectedRegion, events);
    setFilteredEvents(filtered);
  }, [selectedRegion, events]);

  useEffect(() => {
    const filtered = getEventsByCountry(selectedCountry, events);
    setFilteredEvents(filtered);
  }, [selectedCountry, events]);

  useEffect(() => {
    const filtered = getByType(selectedEventType, events);
    setFilteredEvents(filtered);
  }, [selectedEventType, events]);

  const navigateToEvent = (event: any) => {
    navigate(`/events/view?id=${event.id}&location=events`);
  };

  const doesNotHaveData = loading || !events || events.length === 0;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:items-center md:items-center md:flex-row md:justify-end gap-4">
            {/* Responsive Layout Behavior:
              Mobile (<640px):
                - Date picker: 200px width, centered
                - Event Type button: 200px width, centered (always this width to accomodate event types names)
                - Country & Region: Full width, horizontal row below
              
              Small (640px-768px):
                - All items centered horizontally
                - Date picker: Fit content width
                - Event Type, Country, Region: All in single horizontal row below date picker
                
              Medium and up (≥768px):
                - All items in single horizontal row
                - All items right-aligned
                - All items vertically centered
            */}

            {/* Date picker */}
            <div className="w-[95vw] sm:w-[300px] md:w-[200px] mx-auto sm:mx-auto md:mx-0 sm:w-fit">
              <DatePickerComponent handleDateChange={handleDateChange} />
            </div>

            {/* Controls - Event Type, Country, Region */}
            <div className="flex flex-col sm:items-center md:items-start sm:flex-row gap-4">
              <div className="w-[200px] pb-4 sm:p-0 mx-auto sm:mx-auto md:ml-0 md:ml-4 ">
                <Dropdown
                  trigger={
                    <ButtonSlim className="w-full border border-black">
                      {selectedEventType
                        ? getEventTypeLabel(selectedEventType)
                        : "Event Type"}
                    </ButtonSlim>
                  }
                  items={eventTypes.map((eventType) => ({
                    label: eventType.label,
                    onClick: () => setSelectedEventType(eventType.type),
                  }))}
                  showAll={true}
                  selectAll={() => setSelectedEventType(null)}
                />
              </div>
              <div className="flex flex-row justify-center gap-4">
                <Dropdown
                  trigger={
                    <ButtonSlim className="w-[150px]">
                      {selectedCountry
                        ? selectedCountry === "All"
                          ? "Country"
                          : selectedCountry
                        : "Country"}
                    </ButtonSlim>
                  }
                  items={COUNTRIES.map((country) => ({
                    label: country,
                    onClick: () => setSelectedCountry(country),
                  }))}
                  showAll={true}
                  selectAll={() => setSelectedCountry(null)}
                />
                <Dropdown
                  trigger={
                    <ButtonSlim className="w-[150px]">
                      {selectedRegion ? selectedRegion : "Region"}
                    </ButtonSlim>
                  }
                  items={regions.map((region: string) => ({
                    label: region,
                    onClick: () => setSelectedRegion(region),
                  }))}
                  showAll={true}
                  selectAll={() => setSelectedRegion(null)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
        {doesNotHaveData && (
          <div className="text-center text-gray-400 dark:text-white/80">
            Loading...
          </div>
        )}
        {!doesNotHaveData &&
          filteredEvents.map((event) => (
            <Card
              onClick={() => navigateToEvent(event)}
              key={event.id}
              name={event.event_name}
              description={event.event_description}
              dateTime={`${readableDate(
                event.date
              )} ${event.start_time.substring(0, 5)}`}
              providerName={event.providerDetails.provider_name}
              providerLogo={event.providerDetails.provider_logo}
              providerLogoBlurHash={event.providerDetails.provider_logo_blur_hash}
              eventLink={event.event_link}
              country={
                event.event_country
                  ? event.event_country
                  : event.providerDetails.provider_country
              }
              region={
                event.event_region
                  ? event.event_region
                  : event.providerDetails.provider_region
              }
            />
          ))}
      </div>
    </div>
  );
};

export default Events;
